// components/wizard/Authors.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, X, GripVertical, AlertCircle, CheckCircle, UserPlus, Loader } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import axios from "../../../utils/axios";
import Sortable from 'sortablejs';
import { showToast } from '../../../utils/toast';

// Manual Author Input Modal Component (keep your existing one)
const ManualAuthorModal = ({ isOpen, onClose, onSave, existingEmails = [] }) => {
  const [formData, setFormData] = useState({
    prefix: '',
    firstname: '',
    lastname: '',
    othername: '',
    email: '',
    orcid_id: '',
    discipline: '',
    affiliations: '',
    affiliation_country: '',
    affiliation_city: '',
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        prefix: '',
        firstname: '',
        lastname: '',
        othername: '',
        email: '',
        orcid_id: '',
        discipline: '',
        affiliations: '',
        affiliation_country: '',
        affiliation_city: '',
      });
      setErrors({});
      setSaving(false);
    }
  }, [isOpen]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    }
    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else if (existingEmails.includes(formData.email.toLowerCase())) {
      newErrors.email = 'This author has already been added';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, existingEmails]);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const newAuthor = {
        ...formData,
        fullName: `${formData.prefix ? formData.prefix + ' ' : ''}${formData.firstname} ${formData.lastname}`.trim(),
        location: `${formData.affiliation_city || ''}${formData.affiliation_city && formData.affiliation_country ? ', ' : ''}${formData.affiliation_country || ''}`.trim(),
        isCorresponding: false,
        isManuallyAdded: true
      };
      
      onSave(newAuthor);
      onClose();
    } catch (error) {
      console.error('Error saving author:', error);
      setErrors({ submit: 'Failed to save author. Please try again.' });
    } finally {
      setSaving(false);
    }
  }, [formData, validateForm, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="px-6 pt-6 pb-4 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <UserPlus className="w-6 h-6 text-purple-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Add Author Manually</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="mb-6 text-sm text-gray-600">
              Please fill in the author's information below. Fields marked with * are required.
            </p>

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
                <select
                  value={formData.prefix}
                  onChange={(e) => handleChange('prefix', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select Prefix</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.firstname}
                  onChange={(e) => handleChange('firstname', e.target.value)}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.firstname ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.firstname && <p className="mt-1 text-xs text-red-500">{errors.firstname}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => handleChange('lastname', e.target.value)}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.lastname ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.lastname && <p className="mt-1 text-xs text-red-500">{errors.lastname}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Other Name</label>
                <input
                  type="text"
                  value={formData.othername}
                  onChange={(e) => handleChange('othername', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="author@example.com"
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ORCID ID</label>
                <input
                  type="text"
                  value={formData.orcid_id}
                  onChange={(e) => handleChange('orcid_id', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="0000-0000-0000-0000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discipline</label>
                <input
                  type="text"
                  value={formData.discipline}
                  onChange={(e) => handleChange('discipline', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Affiliation/Institution</label>
                <input
                  type="text"
                  value={formData.affiliations}
                  onChange={(e) => handleChange('affiliations', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="University, Institute, or Organization"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.affiliation_city}
                  onChange={(e) => handleChange('affiliation_city', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={formData.affiliation_country}
                  onChange={(e) => handleChange('affiliation_country', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select Country</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="w-full px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-purple-300 sm:w-auto sm:ml-3"
            >
              {saving ? 'Saving...' : 'Add Author'}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-full px-4 py-2 mt-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 sm:w-auto sm:mt-0"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Authors = ({ formData, updateFormData, onNext, onBack, saving }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [correspondingAuthor, setCorrespondingAuthor] = useState(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [localAuthors, setLocalAuthors] = useState([]);
  const { user } = useAuth();
  const coAuthorsContainerRef = useRef(null);
  const sortableInstanceRef = useRef(null);

  // Sync local authors with formData
  useEffect(() => {
    if (formData.authors && formData.authors.length > 0) {
      setLocalAuthors(formData.authors);
    }
  }, [formData.authors]);

  // Initialize SortableJS after the DOM is ready and when co-authors change
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      const container = coAuthorsContainerRef.current;
      const hasCoAuthors = localAuthors.length > 1;
      
      if (container && hasCoAuthors) {
        // Destroy existing instance if it exists
        if (sortableInstanceRef.current) {
          try {
            sortableInstanceRef.current.destroy();
          } catch (err) {
            console.warn('Error destroying sortable:', err);
          }
          sortableInstanceRef.current = null;
        }
        
        // Create new Sortable instance
        try {
          sortableInstanceRef.current = new Sortable(container, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            handle: '.drag-handle',
            onEnd: (evt) => {
              // Get the new order from the DOM
              const items = container.children;
              const newOrder = [];
              
              for (let i = 0; i < items.length; i++) {
                const authorId = items[i].getAttribute('data-author-id');
                const foundAuthor = localAuthors.slice(1).find(a => a.id?.toString() === authorId);
                if (foundAuthor) {
                  newOrder.push(foundAuthor);
                }
              }
              
              // Update authors order
              const updatedAuthors = [localAuthors[0], ...newOrder];
              setLocalAuthors(updatedAuthors);
              updateFormData({ authors: updatedAuthors });
            }
          });
          
          console.log('Sortable initialized successfully');
        } catch (err) {
          console.error('Error initializing Sortable:', err);
        }
      } else if (container && !hasCoAuthors) {
        // Clean up if no co-authors
        if (sortableInstanceRef.current) {
          try {
            sortableInstanceRef.current.destroy();
          } catch (err) {
            console.warn('Error destroying sortable:', err);
          }
          sortableInstanceRef.current = null;
        }
      }
    }, 100); // Small delay to ensure DOM is ready
    
    return () => {
      clearTimeout(timer);
    };
  }, [localAuthors, updateFormData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sortableInstanceRef.current) {
        try {
          sortableInstanceRef.current.destroy();
        } catch (err) {
          console.warn('Error destroying sortable on unmount:', err);
        }
        sortableInstanceRef.current = null;
      }
    };
  }, []);

  // Fetch corresponding author profile
  useEffect(() => {
    const fetchAuthorProfile = async () => {
      if (!user?.email) {
        setFetchingProfile(false);
        setProfileError('No user email found');
        return;
      }

      try {
        setFetchingProfile(true);
        setProfileError(null);
        
        const encodedEmail = encodeURIComponent(user.email);
        const response = await axios.get(`/authorProfileForSearch?encrypted=${encodedEmail}`);
        
        if (response.data.status === 'success' && response.data.accountData) {
          const authorData = response.data.accountData;
          
          const mappedAuthor = {
            id: authorData.id || Date.now(),
            prefix: authorData.prefix || '',
            firstname: authorData.firstname || '',
            lastname: authorData.lastname || '',
            othername: authorData.othername || '',
            email: authorData.email || '',
            orcid_id: authorData.orcid_id || '',
            discipline: authorData.discipline || '',
            affiliations: authorData.affiliations || '',
            affiliation_country: authorData.affiliation_country || '',
            affiliation_city: authorData.affiliation_city || '',
            is_available_for_review: authorData.is_available_for_review || 'no',
            is_editor: authorData.is_editor || 0,
            is_reviewer: authorData.is_reviewer || 0,
            account_status: authorData.account_status || '',
            asfi_membership_id: authorData.asfi_membership_id || '',
            date_joined: authorData.date_joined || '',
            fullName: `${authorData.prefix ? authorData.prefix + ' ' : ''}${authorData.firstname || ''} ${authorData.lastname || ''}`.trim(),
            location: `${authorData.affiliation_city || ''}${authorData.affiliation_city && authorData.affiliation_country ? ', ' : ''}${authorData.affiliation_country || ''}`.trim()
          };
          
          setCorrespondingAuthor(mappedAuthor);
          
          if (localAuthors.length === 0) {
            const newAuthors = [{
              ...mappedAuthor,
              isCorresponding: true
            }];
            setLocalAuthors(newAuthors);
            updateFormData({ authors: newAuthors });
          }
        } else {
          setProfileError(response.data.error || 'Author profile not found');
          const fallbackAuthor = {
            id: Date.now(),
            prefix: user?.prefix || '',
            firstname: user?.firstName || user?.firstname || '',
            lastname: user?.lastName || user?.lastname || '',
            othername: user?.otherName || user?.othername || '',
            email: user?.email || '',
            affiliations: user?.affiliations || user?.affiliation || '',
            affiliation_country: user?.affiliationCountry || user?.affiliation_country || '',
            affiliation_city: user?.affiliationCity || user?.affiliation_city || '',
            orcid_id: user?.orcidId || user?.orcid || '',
            fullName: `${user?.prefix || ''} ${user?.firstName || user?.firstname || ''} ${user?.lastName || user?.lastname || ''}`.trim(),
            location: `${user?.affiliationCity || user?.affiliation_city || ''}${user?.affiliationCity || user?.affiliation_city ? ', ' : ''}${user?.affiliationCountry || user?.affiliation_country || ''}`.trim()
          };
          setCorrespondingAuthor(fallbackAuthor);
          
          if (localAuthors.length === 0) {
            const newAuthors = [{
              ...fallbackAuthor,
              isCorresponding: true
            }];
            setLocalAuthors(newAuthors);
            updateFormData({ authors: newAuthors });
          }
        }
      } catch (error) {
        console.error('Error fetching author profile:', error);
        setProfileError('Failed to fetch author profile. Please try again.');
        
        const fallbackAuthor = {
          id: Date.now(),
          prefix: user?.prefix || '',
          firstname: user?.firstName || user?.firstname || '',
          lastname: user?.lastName || user?.lastname || '',
          othername: user?.otherName || user?.othername || '',
          email: user?.email || '',
          affiliations: user?.affiliations || user?.affiliation || '',
          affiliation_country: user?.affiliationCountry || user?.affiliation_country || '',
          affiliation_city: user?.affiliationCity || user?.affiliation_city || '',
          orcid_id: user?.orcidId || user?.orcid || '',
          fullName: `${user?.prefix || ''} ${user?.firstName || user?.firstname || ''} ${user?.lastName || user?.lastname || ''}`.trim(),
          location: `${user?.affiliationCity || user?.affiliation_city || ''}${user?.affiliationCity || user?.affiliation_city ? ', ' : ''}${user?.affiliationCountry || user?.affiliation_country || ''}`.trim()
        };
        setCorrespondingAuthor(fallbackAuthor);
        
        if (localAuthors.length === 0) {
          const newAuthors = [{
            ...fallbackAuthor,
            isCorresponding: true
          }];
          setLocalAuthors(newAuthors);
          updateFormData({ authors: newAuthors });
        }
      } finally {
        setFetchingProfile(false);
      }
    };

    if (localAuthors.length === 0) {
      fetchAuthorProfile();
    }
  }, [user, localAuthors.length, updateFormData]);

  const addAuthor = useCallback((author) => {
    const newAuthor = { ...author, isCorresponding: false, id: Date.now() };
    const updatedAuthors = [...localAuthors, newAuthor];
    setLocalAuthors(updatedAuthors);
    updateFormData({ authors: updatedAuthors });
  }, [localAuthors, updateFormData]);

  const removeAuthor = useCallback((index) => {
    if (index === 0) return;
    const updatedAuthors = localAuthors.filter((_, i) => i !== index);
    setLocalAuthors(updatedAuthors);
    updateFormData({ authors: updatedAuthors });
  }, [localAuthors, updateFormData]);

  const searchAuthor = useCallback(async () => {
    if (!searchEmail) return;
    
    setSearching(true);
    
    try {
      const encodedEmail = encodeURIComponent(searchEmail);
      const response = await axios.get(`/authorProfileForSearch?encrypted=${encodedEmail}`);
      
      if (response.data.status === 'success' && response.data.accountData) {
        const authorData = response.data.accountData;
        
        const authorExists = localAuthors.some(
          author => author.email?.toLowerCase() === authorData.email?.toLowerCase()
        );
        
        if (authorExists) {
          showToast('This author has already been added', 'error');
          setSearching(false);
          return;
        }
        
        const newAuthor = {
          id: authorData.id || Date.now(),
          prefix: authorData.prefix || '',
          firstname: authorData.firstname || '',
          lastname: authorData.lastname || '',
          othername: authorData.othername || '',
          email: authorData.email || '',
          orcid_id: authorData.orcid_id || '',
          discipline: authorData.discipline || '',
          affiliations: authorData.affiliations || '',
          affiliation_country: authorData.affiliation_country || '',
          affiliation_city: authorData.affiliation_city || '',
          fullName: `${authorData.prefix ? authorData.prefix + ' ' : ''}${authorData.firstname || ''} ${authorData.lastname || ''}`.trim(),
          location: `${authorData.affiliation_city || ''}${authorData.affiliation_city && authorData.affiliation_country ? ', ' : ''}${authorData.affiliation_country || ''}`.trim(),
          isCorresponding: false
        };
        
        addAuthor(newAuthor);
        setSearchEmail('');
      } else {
        setIsManualModalOpen(true);
      }
    } catch (error) {
      console.error('Error searching author:', error);
      setIsManualModalOpen(true);
    } finally {
      setSearching(false);
    }
  }, [searchEmail, localAuthors, addAuthor]);

  const handleManualSave = useCallback((newAuthor) => {
    addAuthor(newAuthor);
    setSearchEmail('');
  }, [addAuthor]);

  const existingEmails = useMemo(() => {
    return localAuthors.map(author => author.email?.toLowerCase()).filter(Boolean);
  }, [localAuthors]);

  const mainAuthor = useMemo(() => {
    return localAuthors[0] || correspondingAuthor;
  }, [localAuthors, correspondingAuthor]);

  if (fetchingProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading author profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6">Author's Information</h3>

      {profileError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">Note:</p>
            <p>{profileError}. Using available user information.</p>
          </div>
        </div>
      )}

      {/* Corresponding Author Section */}
      {mainAuthor && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-purple-900 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Corresponding Author
            </h4>
            <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
              Primary Author
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Prefix</label>
              <input
                type="text"
                value={mainAuthor.prefix || user?.prefix || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">First Name</label>
              <input
                type="text"
                value={mainAuthor.firstname || user?.firstName || user?.firstname || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Last Name</label>
              <input
                type="text"
                value={mainAuthor.lastname || user?.lastName || user?.lastname || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Other Name</label>
              <input
                type="text"
                value={mainAuthor.othername || user?.otherName || user?.othername || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Email</label>
              <input
                type="email"
                value={mainAuthor.email || user?.email || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">ORCID ID</label>
              <input
                type="text"
                value={mainAuthor.orcid_id || user?.orcidId || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Affiliation</label>
              <input
                type="text"
                value={mainAuthor.affiliations || user?.affiliation || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">City</label>
              <input
                type="text"
                value={mainAuthor.affiliation_city || user?.affiliationCity || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Country</label>
              <input
                type="text"
                value={mainAuthor.affiliation_country || user?.affiliationCountry || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">ASFI Membership ID</label>
              <input
                type="text"
                value={mainAuthor.asfi_membership_id || user?.asfiMembershipId || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-purple-700 mb-1">Discipline</label>
              <input
                type="text"
                value={mainAuthor.discipline || user?.discipline || ''}
                readOnly
                className="w-full p-2 bg-white border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Search for Additional Authors */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add Co-Author by Email
        </label>
        <div className="flex space-x-2">
          <input
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Enter author's email"
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && searchAuthor()}
          />
          <button
            onClick={searchAuthor}
            disabled={searching || !searchEmail}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            {searching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Searching...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add Author
              </>
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Search by email address to add co-authors who are registered in the system. If not found, a form will appear to add manually.
        </p>
      </div>

      {/* Manual Author Modal */}
      <ManualAuthorModal
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        onSave={handleManualSave}
        existingEmails={existingEmails}
      />

      {/* Co-Authors List with Sortable */}
      {localAuthors.length > 1 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-700">Co-Authors</h4>
            <p className="text-xs text-gray-500 flex items-center">
              <GripVertical className="w-3 h-3 mr-1" />
              Drag the grip handle to reorder
            </p>
          </div>
          
          <div 
            ref={coAuthorsContainerRef} 
            className="space-y-3"
            style={{ minHeight: '100px' }}
          >
            {localAuthors.slice(1).map((author, idx) => {
              const originalIndex = idx + 1;
              return (
                <div 
                  key={author.id || `author-${idx}`} 
                  data-author-id={author.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start">
                    <div className="drag-handle mr-3 mt-1 cursor-move hover:bg-gray-100 rounded p-1 transition-colors">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Name</label>
                          <p className="text-sm font-medium text-gray-900">
                            {author.fullName || `${author.prefix || ''} ${author.firstname || ''} ${author.lastname || ''}`.trim()}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Email</label>
                          <p className="text-sm text-gray-700">{author.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">ORCID</label>
                          <p className="text-sm text-gray-700">{author.orcid_id || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Affiliation</label>
                          <p className="text-sm text-gray-700">{author.affiliations || 'Not specified'}</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Location</label>
                          <p className="text-sm text-gray-700">{author.location || `${author.affiliation_city || ''}${author.affiliation_city && author.affiliation_country ? ', ' : ''}${author.affiliation_country || ''}`.trim() || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeAuthor(originalIndex)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove author"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info about corresponding author */}
      {localAuthors.length === 1 && (
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 flex items-start">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
            You are the corresponding author. You can add co-authors using the search above.
          </p>
        </div>
      )}

      <p className="text-sm text-red-500 mb-6">* Indicates Mandatory Fields</p>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        
        <button
          onClick={onNext}
          disabled={saving}
          className="px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin inline mr-2" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default Authors;